/**
 * Trend category → display (arrow, color, CSS class).
 * D-17: EU goal-level trend uses 5 categories including Stagnation (Pietro P4).
 * EU indicator-level trend still uses 5 categories including Stagnation (OQ-3).
 */

export const EU_GOAL_TREND_LABELS = [
  'Significant progress',
  'Moderate progress',
  'Stagnation',
  'Moderate regression',
  'Significant regression',
] as const;

export const EU_INDICATOR_TREND_LABELS = [
  'Significant progress',
  'Moderate progress',
  'Stagnation',
  'Moderate regression',
  'Significant regression',
] as const;

export const UN_TREND_LABELS = [
  'On track or target met',
  'Moderate progress, acceleration needed',
  'Marginal progress, significant acceleration needed',
  'Stagnation',
  'Regression',
  'Insufficient data',
] as const;

const TREND_COLORS = {
  onTrack: '#16a34a',
  moderateProgress: '#fcc30c',
  stagnation: '#9ca3af',
  moderateRegression: '#f67c01',
  regression: '#d3302f',
  insufficient: '#9ca3af',
};

export type TrendArrowDisplay = {
  arrow: string;
  color: string;
  className: string;
};

/** Map API trend category to arrow, colour, and CSS class. */
export function getTrendArrow(category: string | null | undefined): TrendArrowDisplay {
  if (!category) {
    return { arrow: '—', color: TREND_COLORS.insufficient, className: 'trend-arrow-default' };
  }

  const cat = String(category).toLowerCase();

  if (cat.includes('insufficient') || cat.includes('invalid')) {
    return { arrow: '—', color: TREND_COLORS.insufficient, className: 'trend-arrow-default' };
  }

  // D-17: moderate regression (↘) before generic regression; stagnation is grey (home legend).
  if (cat.includes('moderate regression')) {
    return {
      arrow: '↘',
      color: TREND_COLORS.moderateRegression,
      className: 'trend-arrow-moderate-regression',
    };
  }

  if (cat.includes('regression') || cat.includes('decreasing')) {
    return { arrow: '↓', color: TREND_COLORS.regression, className: 'trend-arrow-regression' };
  }

  if (cat.includes('stagnation') || cat.includes('stagnating') || cat.includes('neutral')) {
    return { arrow: '→', color: TREND_COLORS.stagnation, className: 'trend-arrow-stagnation' };
  }

  if (cat.includes('on track') || cat.includes('target met') || cat.includes('significant progress')) {
    return { arrow: '↑', color: TREND_COLORS.onTrack, className: 'trend-arrow-on-track' };
  }

  if (cat.includes('moderate progress') || (cat.includes('moderate') && cat.includes('acceleration'))) {
    return { arrow: '↗', color: TREND_COLORS.moderateProgress, className: 'trend-arrow-moderate-progress' };
  }

  if (cat.includes('marginal')) {
    return { arrow: '→', color: TREND_COLORS.stagnation, className: 'trend-arrow-marginal' };
  }

  if (cat.includes('moderate')) {
    return { arrow: '↗', color: TREND_COLORS.moderateProgress, className: 'trend-arrow-moderate-progress' };
  }

  return { arrow: '—', color: TREND_COLORS.insufficient, className: 'trend-arrow-default' };
}

export function getTrendArrowClass(category: string | null | undefined): string {
  return getTrendArrow(category).className;
}

/** Map EU goal trend category to chart band code (0 = no data, 1 = regression … 4 = significant progress). */
export function euTrendCategoryToCode(category: string | null | undefined): number {
  if (!category) return 0;
  const cat = String(category).toLowerCase();
  if (cat.includes('insufficient') || cat.includes('no data')) return 0;
  if (cat.includes('regression') || cat.includes('decreasing')) return 1;
  if (cat.includes('stagnation') || cat.includes('stagnating')) return 2;
  if (cat.includes('significant progress') || cat.includes('on track')) return 4;
  if (cat.includes('moderate progress')) return 3;
  return 0;
}

/** Map API trend category to chart band code (UN or EU). */
export function trendCategoryToChartCode(
  category: string | null | undefined,
  goalType?: string | null
): number {
  if (goalType === 'eu') {
    return euTrendCategoryToCode(category);
  }
  if (!category) return 0;
  const cat = String(category).toLowerCase();
  if (cat.includes('on track') || cat.includes('target met')) return 4;
  if (cat.includes('moderate')) return 3;
  if (cat.includes('marginal') || cat.includes('stagnation') || cat.includes('stagnating')) return 2;
  if (cat.includes('regression') || cat.includes('decreasing')) return 1;
  if (cat.includes('insufficient data') || cat.includes('no data')) return 0;
  const num = parseInt(category, 10);
  if (!isNaN(num) && num >= 0 && num <= 4) return num;
  return 0;
}

export const EU_TREND_CHART_LABELS: Record<number, string> = {
  0: 'Insufficient data',
  1: 'Significant regression',
  2: 'Stagnation',
  3: 'Moderate progress',
  4: 'Significant progress',
};

/** Legend labels for goal-grid progress key (goals and indicators both use 5 EU bands). */
export function getTrendLegendLabels(goalType?: string | null): readonly string[] {
  if (goalType === 'eu') {
    return [...EU_GOAL_TREND_LABELS, 'Insufficient data'];
  }
  return UN_TREND_LABELS;
}
